import type { Preferences } from '@cobliteam/common-types';
import { useLocalStorage } from '@cobliteam/hooks';
import { useEffect, useState } from 'react';

const preferenceKeys: Array<keyof Preferences> = [
  'displayLicensePlate',
  'displayVehiclesCluster',
  'displayServiceCard',
  'displayMonitoringGeofences',
  'displayMonitoringGeofencesForceVisibility',
  'displayTrackingGeofences',
  'isOpenSidebar',
  'idleEngineReportThreshold',
  'reportsMailList',
  'geofenceSuccessModal',
  'geofenceTourSeen',
  'hideGeofenceFormatBanner',
  'useMapMatch',
  'isFirstTriggersAccess',
  'hideScheduledMaintenanceBanner',
  'hideVehicleGroupsPreferencesBanner',
  'hideTriggerEventsInfoBanner',
  'hideNewTriggersFeatureInfoBanner',
  'hideIdleEngineOnCalibrationBanner',
  'showSignalTour',
  'hasAgreedNominationNotification',
  'showUserFeedbackTypeform',
  'hasCompletedTripImageHistoryTutorial',
  'showIncidentBanner',
  'openSiteVisibility',
  'disableAnsweredUpsellResearchDialog',
  'answeredUpsellResearchDialog',
  'shownTwoTimesThisWeek',
  'upsellResearchDialogLastShownDate',
  'hideCoblicamPLGIdleEngine',
  'hideCoblicamPLGFleetSafety',
  'safetyRankingTypePage',
  'vehicleGroupIds',
  'enableVehicleDriverAllowList',
  'mediaEventLiveUpdate',
  'onboarding',
  'typeformSurvey',
  'hideDriverAssociationPastConfirmation',
  'globalSearchNew',
  'appJoyride',
  'insights',
];

const PreferencesManager = () => {
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const [preferencesState, setPreferencesState] = useState<Record<string, any>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  useEffect(() => {
    if (preferences) {
      setPreferencesState({ ...preferences });
    }
  }, [preferences]);

  const handleTogglePreference = (key: string, value: any) => {
    if (!preferences) return;

    const newPreferences = { ...preferences } as Record<string, any>;

    if (typeof value === 'boolean') {
      newPreferences[key] = !value;
    } else if (value === undefined) {
      newPreferences[key] = true;
    } else {
      // If it's not a boolean, we'll just remove it
      delete newPreferences[key];
    }

    setPreferences(newPreferences as Preferences);
  };

  const handleCycleState = (key: string, value: any) => {
    if (!preferences) return;

    const newPreferences = { ...preferences } as Record<string, any>;

    if (value === undefined) {
      // Not set -> true
      newPreferences[key] = true;
    } else if (value === true) {
      // true -> false
      newPreferences[key] = false;
    } else if (value === false) {
      // false -> not set (removed)
      delete newPreferences[key];
    } else {
      // If it's not a boolean, we'll just remove it
      delete newPreferences[key];
    }

    setPreferences(newPreferences as Preferences);
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all preferences? This cannot be undone.')) {
      localStorage.removeItem('preferences');
      window.location.reload();
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderPreferenceValue = (value: any): string => {
    if (value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object' && value !== null) return 'Object';
    return String(value);
  };

  const renderDetailedValue = (key: string, value: any) => {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    return (
      <div className='mt-2 border-gray-2 border-l-2 pl-4'>
        <pre className='max-h-48 overflow-auto rounded bg-gray-50 p-2 text-xs'>{JSON.stringify(value, null, 2)}</pre>
      </div>
    );
  };

  // Get all keys including those from the interface and those in localStorage
  const getAllPreferenceKeys = () => {
    const allKeys = new Set([...preferenceKeys, ...Object.keys(preferencesState)]);

    return Array.from(allKeys)
      .filter((key) => {
        // Apply search filter
        if (searchTerm && !key.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }

        // Apply "missing only" filter
        if (showMissingOnly && preferencesState[key] !== undefined) {
          return false;
        }

        return true;
      })
      .sort();
  };

  if (!preferences) {
    return (
      <div className='p-4'>
        <h1 className='mb-4 font-bold text-xl'>Preferences Manager</h1>
        <p className='mb-4'>No preferences found in localStorage.</p>
        <button type='button' onClick={() => setPreferences({} as Preferences)} className='rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600'>
          Initialize Preferences
        </button>
      </div>
    );
  }

  return (
    <div className='p-2'>
      <div className='mb-2 flex items-center justify-between'>
        <h1 className='font-bold text-lg'>Preferences Manager</h1>
        <button type='button' onClick={handleReset} className='rounded bg-red-500 px-2 py-1 text-white text-xs hover:bg-red-600'>
          Reset All
        </button>
      </div>
      <div className='mb-2 flex items-center gap-2'>
        <div className='flex-1'>
          <input
            type='text'
            placeholder='Search preferences...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full rounded border border-gray-300 px-2 py-1 text-sm'
          />
        </div>
        <div className='flex items-center'>
          <input type='checkbox' id='showMissingOnly' checked={showMissingOnly} onChange={() => setShowMissingOnly(!showMissingOnly)} className='mr-1' />
          <label htmlFor='showMissingOnly' className='text-sm'>
            Show not set only ({getAllPreferenceKeys().filter((key) => preferencesState[key] === undefined).length})
          </label>
        </div>
      </div>

      <div className='overflow-hidden rounded bg-white shadow'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-2 py-1 text-left font-medium text-gray-500 text-xs uppercase tracking-wider'>Key</th>
              <th className='px-2 py-1 text-left font-medium text-gray-500 text-xs uppercase tracking-wider'>Value</th>
              <th className='px-2 py-1 text-left font-medium text-gray-500 text-xs uppercase tracking-wider'>Toggle</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white'>
            {getAllPreferenceKeys().map((key) => {
              const value = preferencesState[key];
              const isUndefined = value === undefined;
              const isBoolean = typeof value === 'boolean';

              return (
                <tr key={key} className={isUndefined ? 'bg-gray-50' : ''}>
                  <td className='whitespace-nowrap px-2 py-1 font-medium text-gray-900 text-xs'>
                    {key}
                    {isUndefined && <span className='ml-1 text-gray-400 text-xs'>(not set)</span>}
                  </td>
                  <td className='px-2 py-1 text-gray-500 text-xs'>
                    <div className='flex items-center'>
                      <span className='whitespace-nowrap'>{renderPreferenceValue(value)}</span>
                      {typeof value === 'object' && value !== null && (
                        <button type='button' onClick={() => toggleExpand(key)} className='ml-1 text-blue-500 text-xs hover:text-blue-700'>
                          {expandedItems[key] ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                    {expandedItems[key] && renderDetailedValue(key, value)}
                  </td>
                  <td className='whitespace-nowrap px-2 py-1 text-xs'>
                    {isBoolean || isUndefined ? (
                      <div className='flex items-center gap-1'>
                        <input type='checkbox' checked={value === true} onChange={() => handleTogglePreference(key, value)} className='h-4 w-4 cursor-pointer' />
                        <button
                          type='button'
                          onClick={() => handleCycleState(key, value)}
                          className='rounded bg-gray-200 px-2 py-0.5 text-xs hover:bg-gray-300'
                          title='Cycle states: true → false → not set'
                        >
                          {value === true ? '✓' : value === false ? '✗' : '∅'}
                        </button>
                      </div>
                    ) : (
                      <button type='button' onClick={() => handleTogglePreference(key, value)} className='rounded bg-red-500 px-2 py-0.5 text-white text-xs hover:bg-red-600'>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreferencesManager;
